// /brokers/$id — one broker node, for exchange governance (SM-716,
// SM-621 phase 08a).
//
// The header carries the node's name, its own and effective status and
// the lifecycle verbs this operator may run on it now (approve, pause,
// emergency disable, resume, begin closure, close — each a confirm
// dialog with a mandatory reason that shows broker-service's audit
// reference). The tabs below are the node's governance views:
//
//   Overview   status and what it permits, profile, business settings,
//              root→node path, provider integration status
//   Hierarchy  the subtree as an expandable tree (effective statuses)
//   Staff      memberships; invite / suspend / resume / remove / re-role
//   Accounts   the broker accounts it services, directly or in its subtree
//   Funding    its accounts per currency and the funding transfers
//   Risk       exposure and limit terms, in GOLD (the only exposure currency)
//   Fees       a root's fee plans, versions and assignments (BROKER_FEE_VIEW;
//              writes BROKER_FEE_MANAGE); on every node its effective fee
//              schedule, read from broker-service
//   Revenue share  a root's share plans, versions and assignments
//              (BROKER_REVENUE_VIEW; writes BROKER_REVENUE_SHARE_MANAGE); on
//              every node the split that applies there
//   Audit      the broker audit trail (BROKER_AUDIT_VIEW)
//
// The tab lives in the URL so a view is linkable.
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { BrokerAccountsTab } from "@/components/brokers/BrokerAccountsTab";
import { BrokerAuditTab } from "@/components/brokers/BrokerAuditTab";
import { BrokerStatusBadges } from "@/components/brokers/BrokerBadges";
import { BrokerFeesTab } from "@/components/brokers/BrokerFeesTab";
import { BrokerFundingPanel } from "@/components/brokers/BrokerFundingPanel";
import { BrokerHierarchyTab } from "@/components/brokers/BrokerHierarchyTab";
import { BrokerLifecycleControls } from "@/components/brokers/BrokerLifecycleControls";
import { BrokerOverviewTab } from "@/components/brokers/BrokerOverviewTab";
import { BrokerRevenueShareTab } from "@/components/brokers/BrokerRevenueShareTab";
import { BrokerRiskPanel } from "@/components/brokers/BrokerRiskPanel";
import { BrokerStaffTab } from "@/components/brokers/BrokerStaffTab";
import { brokerName } from "@/components/brokers/governance";
import { Badge } from "@/components/ui/Badge";
import { EmptyState } from "@/components/ui/EmptyState";
import { ErrorPanel } from "@/components/ui/ErrorPanel";
import { Skeleton } from "@/components/ui/Skeleton";
import { useMyPerms } from "@/hooks/useMyPerms";
import { brokerKeys, fetchBroker } from "@/lib/broker-queries";
import { cn } from "@/lib/cn";
import { Perm, type PermCode } from "@/lib/perms";

type Tab = "overview" | "hierarchy" | "staff" | "accounts" | "funding" | "risk" | "fees" | "revenue-share" | "audit";

const TABS: { key: Tab; label: string; perm: PermCode }[] = [
  { key: "overview", label: "Overview", perm: Perm.BrokerView },
  { key: "hierarchy", label: "Hierarchy", perm: Perm.BrokerView },
  { key: "staff", label: "Staff", perm: Perm.BrokerView },
  { key: "accounts", label: "Accounts", perm: Perm.BrokerView },
  { key: "funding", label: "Funding", perm: Perm.BrokerView },
  { key: "risk", label: "Risk", perm: Perm.BrokerExposureView },
  { key: "fees", label: "Fees", perm: Perm.BrokerFeeView },
  { key: "revenue-share", label: "Revenue share", perm: Perm.BrokerRevenueView },
  { key: "audit", label: "Audit", perm: Perm.BrokerAuditView },
];

/** Why a tab the operator may not read renders no data. */
const NO_TAB_ACCESS: Record<Tab, string> = {
  overview: "You need the BROKER_VIEW permission to see brokers.",
  hierarchy: "You need the BROKER_VIEW permission to see brokers.",
  staff: "You need the BROKER_VIEW permission to see brokers.",
  accounts: "You need the BROKER_VIEW permission to see brokers.",
  funding: "You need the BROKER_VIEW permission to see brokers.",
  risk: "You need the BROKER_EXPOSURE_VIEW permission to see a broker's exposure.",
  fees: "You need the BROKER_FEE_VIEW permission to see a broker's fee plans.",
  "revenue-share": "You need the BROKER_REVENUE_VIEW permission to see a broker's revenue-share plans.",
  audit: "You need the BROKER_AUDIT_VIEW permission to read a broker's audit trail.",
};

interface Search {
  tab?: Tab;
}

export const Route = createFileRoute("/_app/brokers/$id")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    tab: TABS.find((t) => t.key === s.tab)?.key,
  }),
  component: BrokerDetailPage,
});

function BrokerDetailPage() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const perms = useMyPerms();
  const canView = perms.has(Perm.BrokerView);
  const tab: Tab = search.tab ?? "overview";

  const q = useQuery({
    queryKey: brokerKeys.broker(id),
    queryFn: () => fetchBroker(id),
    enabled: canView,
  });
  const b = q.data;

  if (perms.loaded && !canView) {
    return <EmptyState title="No access" description="You need the BROKER_VIEW permission to see brokers." />;
  }

  const tabs = TABS.filter((t) => perms.has(t.perm));
  const tabAllowed = tabs.some((t) => t.key === tab);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-2xs text-muted">
        <Link to="/brokers" className="hover:underline">
          Broker directory
        </Link>
        <span aria-hidden>›</span>
        <span className="font-mono">{id}</span>
      </div>

      {q.isLoading || !perms.loaded ? <Skeleton className="h-20" /> : null}
      {q.isError ? <ErrorPanel error={q.error} reset={() => void q.refetch()} /> : null}

      {b ? (
        <>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0 space-y-1.5">
              <h1 className="font-display text-2xl tracking-tight">{brokerName(b)}</h1>
              <div className="flex flex-wrap items-center gap-2">
                <BrokerStatusBadges broker={b} />
                {!b.parent_broker_id ? <Badge tone="accent">Root</Badge> : null}
                {b.is_system ? <Badge tone="accent">System broker</Badge> : null}
                <span className="font-mono text-2xs text-muted">{b.broker_id}</span>
              </div>
              {b.is_system ? (
                <p className="text-2xs text-muted">
                  The SYSTEM_BROKER services every user who did not arrive through a broker. It cannot be paused,
                  closed or moved.
                </p>
              ) : null}
            </div>
            <BrokerLifecycleControls broker={b} />
          </div>

          <div role="tablist" aria-label="Broker views" className="flex flex-wrap border-b border-line-1">
            {tabs.map((t) => {
              const active = t.key === tab;
              return (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => void navigate({ search: (s) => ({ ...s, tab: t.key === "overview" ? undefined : t.key }) })}
                  className={cn(
                    "border-b-2 px-3 py-2 text-2xs font-semibold uppercase tracking-wide transition-colors",
                    active ? "border-neon text-t-1" : "border-transparent text-t-3 hover:text-t-1",
                  )}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          <div role="tabpanel">
            {!tabAllowed ? (
              <EmptyState title="No access" description={NO_TAB_ACCESS[tab]} />
            ) : (
              {
                overview: () => <BrokerOverviewTab broker={b} />,
                hierarchy: () => <BrokerHierarchyTab broker={b} />,
                staff: () => <BrokerStaffTab broker={b} />,
                accounts: () => <BrokerAccountsTab broker={b} />,
                funding: () => <BrokerFundingPanel brokerId={b.broker_id} />,
                risk: () => <BrokerRiskPanel brokerId={b.broker_id} rootBrokerId={b.root_broker_id} />,
                fees: () => <BrokerFeesTab broker={b} />,
                "revenue-share": () => <BrokerRevenueShareTab broker={b} />,
                audit: () => <BrokerAuditTab brokerId={b.broker_id} />,
              } satisfies Record<Tab, () => React.ReactElement>
            )[tab]()}
          </div>
        </>
      ) : null}
    </div>
  );
}
